import store from '../../store/index.js';
import config from '../../client-config.js';
import superagent from 'superagent';
import { matchPath } from 'react-router-dom';
import * as ROUTES from 'constants/routes.js';
import localPersistenceHelper from 'helpers/localPersistenceHelper.js';
import { DEV_SETTINGS, DEV_SETTINGS_LOCAL_STORAGE_KEY } from 'constants/devSettings.js';
import { isEuProject } from 'constants/gcp.js';
import { isEqual } from 'helpers/isEqual.js';
import { initialize, TextCapture, GuideMarkdown } from '@pendo/agent';

const DEMO_ACCOUNT_ORG = 'ofMyeJZLSHhPq3z1O0KA';
const excludedClientsForPendo = [
  'dcd2kvR7GiSR0aNcYa7y',
  'HnJBvZfjQAwZLvntKb5b',
  'Kel4qBsz6K18HjO2ELks',
  'RQCwZ0iJiNGU2jENNyBx',
  'FdiSJfgoUrfawjGtgwMf',
  'D37m5CFImXb0EsDyC3Sh',
  'j1rfLlkmF8bnTaYTDc4b',
  'e0CIYVRYVs59ObHYLBtp',
  '43quzqOIKI91PsQ6j1G5',
  '3bRfUkYL8mRrPFXuVEGx',
  'JCGAvzuZgseoE8PfWEMn',
  'lJCCQDk1UrEWUoVKUKGP',
  'ER9z0VeVeLFTCqc5KKEh',
  '6ta3r6aFuHnoMHOedK4x',
  'kkqRkmbKsMH1UglcaIRj',
  'XfJcaqTDr4E5Nkg6QKwV',
  'vTC7QG549tj6h6KRSYnn',
  'cBNHVeHkG0rJChnb28dN',
  'yV0sKRQAbvuZsbfytMda',
  'DZ6wxtyvCvRB5MgI0jy5',
  'o0KFQ3X6Vu4kffTlO1PY',
  'RkT12Q5bXFpoWHpjxQKM',
  'EvUSN2qjqKwLVefGBe9j',
  'mqTsLd59qW8v0ivywm3W',
  'TH54Nnbbt92wGOhcjec3',
  '5hrsj7Zbphz4YMkBZPkF',
]; // Add clientIds of the ones that have opted out from Pendo as sub-processor

class TrackingService {
  constructor(enableLogging) {
    this._authUser = null;
    this._userId = null;
    this._currentOrgId = null;
    this._clientId = null;
    this._plan = null;
    this._enableLogging = !!enableLogging;
    this._lastPageVisit = null;
    this.handleState(store.getState());
    store.subscribe(() => {
      this.handleState(store.getState());
    });
    let trackingDeactivated = false;
    const shouldDeactivateTracking = window.location?.search?.includes('deactivateTracking');
    if (window.sessionStorage) {
      if (shouldDeactivateTracking) {
        localPersistenceHelper.setItem({ key: 'trackingDeactivated', data: true, storage: localPersistenceHelper.storages.SESSION_STORAGE });
      } else {
        trackingDeactivated = localPersistenceHelper.getItem({ key: 'trackingDeactivated', storage: localPersistenceHelper.storages.SESSION_STORAGE });
      }
    }
    this.deactivateTracking = shouldDeactivateTracking || trackingDeactivated;
  }

  handleState({ sessionState: { authUser, currentOrgId, selectedProduct, userAccess } = {}, ui: { masqueradeMode } = {} }) {
    const currentMembership = typeof authUser?.membership === 'function' && authUser.membership(currentOrgId);
    const currentOrg = currentMembership?.org;
    this._currentOrg = currentOrg || masqueradeMode?.org;
    this._clientId = this._currentOrg?.clientId || null;
    this._userAccess = userAccess || null;
    // Mabye refactor to be list of tags instead of a string, since orgs can be a combination of these
    // or just have type like this, then internal(dev) true/false
    if (currentOrg?.type) {
      this._orgType = currentOrg.type; // Intended for trial/sandbox/default/partner
    } else if (currentOrg?.isTrial) {
      this._orgType = 'trial';
    } else {
      this._orgType = 'default';
    }
    if (isEuProject && this._orgType !== 'trial') {
      this._canTrackToGA = false;
      // We are only allowed to track to GA in EU for free trial users
    } else {
      // This is to turn off GA tracking if navigating to org within EU that is not trial
      this._canTrackToGA = true;
    }

    this._authUser = authUser;
    this._currentOrgId = currentOrgId;
    this._selectedProduct = selectedProduct;
    if (this._authUser) {
      this._userId = this._authUser.uid;
      this._isBambuserUser = this._authUser.email.endsWith('@bambuser.com');
    }
    if (currentOrgId === DEMO_ACCOUNT_ORG) {
      this._plan = 'demo';
    }
  }

  /**
   * Tracks a click event.
   * @param {Object} data
   * @param {string} data.clickId - The id of the clicked element.
   * @param {string} data.eventType - The type of event.
   * @param {string} data.pageName - The name of the page.
  */
  trackClick(data) {
    const { clickId, ...restData } = data;
    const pageName = this._lastPageVisit?.pageName || this._lastPageVisit?.path;
    this.track({
      ...restData,
      eventType: 'gtm.click',
      'gtm.elementId': clickId,
      pageName,
    }, {
      BigQuery: false,
      GA: true,
    });
    this.track({
      ...restData,
      eventType: 'click',
      clickId,
      pageName,
    }, {
      GA: false,
      BigQuery: true,
    });
  }

  /**
   * Tracks page visit.
   * @param {object} params e.g. { orgId: 'hDGPktZ2H5UZ25Aq8jD6', selectedProduct: 'live' }
   * @param {string} path e.g. /:orgId/:selectedProduct
   * @param {string} url e.g. /hDGPktZ2H5UZ25Aq8jD6/live
   * @param {string} pageName e.g. ORG_SHOW
  */
  trackPageVisit = ({ params, path, pageName, url }) => {
    this._lastPageVisit = { params, path, url, pageName };
    this.track({
      eventType: 'app:visit-page',
      pageParams: params,
      pagePath: path,
      pageUrl: url,
      pageName,
    });
  }

  /**
   * Tracking point with "data". Tracks an event by notifying tracking services such as BigQuery and Google Analytics.
   * @param {Object} data
   * @param {Object} trackingServices - Services to notify about the given event.
   * @param {boolean} [trackingServices.BigQuery = true] - Tracks to BigQuery if set to true (default: true)
   * @param {boolean} [trackingServices.GA = true] - Tracks to Google Analytics if set to true (default: true)
   */
  track(data, trackingServices = { BigQuery: true, GA: true, Pendo: true }) {
    if (this.deactivateTracking) return;
    const compiledData = {
      ...data,
      source: 'dashboard',
      customerId: this._currentOrgId,
      userId: this._userId,
      userDevice: window.navigator.userAgent,
      userIsBambuserUser: this._isBambuserUser,
      userIsSuperadmin: !!this._authUser?.isSuperadmin,
      orgType: this._orgType,
      selectedProduct: this._selectedProduct,
    };

    const { params: { showId = null } = {} } = matchPath(window.location.pathname, { path: ROUTES.ORG_SHOW }) || {};

    if (showId) {
      compiledData.showId = showId;
    }

    if (compiledData.eventType) {
      // kebab-case eventType
      if (/[A-Z][A-Z]/.test(compiledData.eventType)) compiledData.eventType = compiledData.eventType.toLowerCase();
      compiledData.eventType = compiledData.eventType
        .replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)
        .replace(/_/g, '-');
    }

    if (trackingServices.BigQuery) {
      this.trackToBQ(compiledData);
    }

    if (trackingServices.GA) {
      this.trackToGA(compiledData);
    }

    if (this._selectedProduct && trackingServices.Pendo) {
      const currentMembership = typeof this._authUser?.membership === 'function' && this._authUser.membership(this._currentOrgId);
      const currentOrg = this._currentOrg;
      if (!currentOrg) return; // When in superadmin or client admin pages we don't have currentOrg
      if (excludedClientsForPendo.includes(this._clientId)) return; // Don't track to Pendo for excluded clients
      const invitedAt = currentMembership?.user?.invitedAt?.toDate ? currentMembership.user.invitedAt.toDate() : null;

      const clientSubscriptionStorageKey = `client-${this._clientId}-subscriptions`;
      const clientSubscriptionResult = localPersistenceHelper.getItem({ key: clientSubscriptionStorageKey, prefix: '', storage: localPersistenceHelper.storages.LOCAL_STORAGE });
      const clientSubscriptionInfo = clientSubscriptionResult?.subscriptionInfo || [];

      const clientAccess = this._userAccess?.clients?.[this._clientId] || {};
      const clientRoles = this._userAccess?.clients?.[this._clientId]?.roles || [];
      const hasClientLevelUsagePermission = clientAccess?.permissions?.['USAGE_CLIENT']?.includes('read') || false;
      const pendoUserData = {
        visitor: {
          id: this._userId,
          createdAt: this._authUser?.creationTime ? new Date(this._authUser.creationTime) : null, // Time this user was first ever created
          invitedAt, // Time this user was invited to currentOrg
          email: this._authUser?.email,
          name: this._authUser?.displayName,
          isSuperadmin: this._authUser?.isSuperadmin || false,
          hasClientUsagePermission: clientRoles.includes('clientOwner')|| hasClientLevelUsagePermission,
          userRoles: currentMembership?.user?.roles || [], // This does not really get scoped per account... pendo picks latest, works in most real cases
        },
        account: {
          id: this._currentOrgId,
          name: currentOrg.title || 'Unknown Org',
          bamhubType: this._orgType, // trial/sandbox/default/partner
          region: isEuProject ? 'EU' : 'US',
          clientId: this._clientId,
          clientName: clientSubscriptionInfo?.client?.name || 'Unknown Client',
          unpaidInvoice: !!clientSubscriptionInfo?.client?.shouldShowInvoiceBanner,
          salesforceUsageAccount: clientSubscriptionInfo?.client?.salesforceUsageAccount || null,
          maxUsageRatio: clientSubscriptionInfo?.maxUsageRatio, // 0.0 and up 1.0 means 100% usage, above means they exceeded their plan
          hasSubscriptionAdmin: clientSubscriptionInfo.admins?.length > 0,
        },
      };

      if (compiledData.eventType === 'identify') {
        if (!isEqual(this._currentPendoIdentity, pendoUserData)) {
          this.trackToPendo(pendoUserData, 'identify');
          this._currentPendoIdentity = pendoUserData;
        }
      } else {
        this.trackToPendo(data);
      }
    }
  }

  trackToPendo(data, method = 'track') {
    if (config.pendoApiKey) {
      if (this.pendoPromise) {
        this.pendoPromise.then(() => {
          if (method === 'identify') {
            window.pendo.identify(data);
          } else if (window.pendo.track) {
            window.pendo.track(data.eventType, data);
          }
        }).catch((err) => {
          if (process.env.NODE_ENV === 'development') return;
          console.warn('Tracking to Pendo failed', err);
        });
      }
      if (!window.pendo && method === 'identify') {
        this.pendoPromise = initialize({
          apiKey: config.pendoApiKey,
          env: 'eu',
          globalKey: 'pendo',
          ...data,
          plugins: [TextCapture, GuideMarkdown],
        });
      }
    }
  }

  /**
   * Push "data" to Big Query for tracking.
   * @param {Object} data
   */
  trackToBQ(data) {
    const dataString = JSON.stringify(data);
    const endpoint = `${config.metricsBaseUrl}/metric`;
    let sendBeaconSuccessful = false;

    if (navigator && navigator.sendBeacon) {
      try {
        sendBeaconSuccessful = navigator.sendBeacon(endpoint, dataString);
      } catch (err) {
        // Let's use the fallback below
      }
    }

    if (!sendBeaconSuccessful) {
      superagent
        .post(endpoint)
        .send({
          ...data,
        })
        .catch((err) => {
          console.error(`Unsuccessful tracking: ${err}`, data);
        });
    }

    if (this._enableLogging) {
      console.log(
        `%cTracking Point\n%cBigQuery%c\n👉 ${data.eventType}\n`,
        'background: #222; color: #fff; font-weight: bold; font-size: 14px',
        'background: #222; color: #3f6bde; font-weight: bold; font-size: 14px',
        'background: #222; color: blueviolet; font-weight: normal; font-size: 12px',
        data,
      );
    }
  }

  /**
   * Push "data" to Google Analytics data layer if it exist on the window object.
   * @param {Object} data
   */
  trackToGA(data) {
    if (window && !window.dataLayer && config.gtmTagId && this._canTrackToGA) {
      // Create new GTM instance by adding the window variable and loading the GTM script
      window.dataLayer = [];
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const firstScriptNode = document.getElementsByTagName('script')[0];
      const gtmScriptNode = document.createElement('script');
      gtmScriptNode.async = true;
      gtmScriptNode.src = `https://www.googletagmanager.com/gtm.js?id=${config.gtmTagId}`;
      if (firstScriptNode?.parentNode) {
        firstScriptNode.parentNode.insertBefore(gtmScriptNode, firstScriptNode);
      } else {
        document.head.appendChild(gtmScriptNode);
      }
    }

    if (window && window.dataLayer && window.dataLayer.push) {
      window.dataLayer.push({
        ...data,
        event: data.eventType,
        clientId: this._clientId,
      });
    }
  }

  /**
   * Clears all user data values stored in TrackingService.
   */
  clearUserData(callback) {
    this._authUser = null;
    this._userId = null;
    this._currentOrgId = null;
    this._currentOrg = null;
    this._clientId = null;
    this._isBambuserUser = null;
    this._orgType;
    this._selectedProduct = null;
    this._currentPendoIdentity = null;
    if (window && window.pendo) {
      window.pendo.clearSession();
    }
    typeof callback === 'function' && callback();
  }

  /**
   * Helper method to build an event type used for tracking with this format module:action:[meta]
   * @param {string} module
   * @param {string} action
   * @param {string} [meta]
   * @returns {string} module:action:[meta]
   */
  buildTrackingEventName(module, action, meta) {
    let eventType = `${module}:${action}`;
    if (meta) {
      eventType += `:${meta}`;
    }
    return eventType;
  }
}

const devSettings = localPersistenceHelper.getItem({
  key: DEV_SETTINGS_LOCAL_STORAGE_KEY,
}) || [];

export const trackingService = new TrackingService(devSettings.includes(DEV_SETTINGS.LOG_BQ_TRACKING_POINTS));
